import React, { useState } from "react";
import { useBilling } from "../store/BillingContext";
import { formatDate, money } from "../lib/utils";
import { AudioExpenseModal } from "../components/expenses/AudioExpenseModal";

export const ExpensesView: React.FC = () => {
  const { state, setActiveForm, deleteExpense } = useBilling();
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [recurrenceFilter, setRecurrenceFilter] = useState<"all" | "recurring" | "one_time">("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [isVoiceModalOpen, setIsVoiceModalOpen] = useState(false);

  const totalExpense = state.expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);

  // Recurring Subscriptions calculation
  const recurringExpenses = state.expenses.filter(
    (e) => e.recurrence && e.recurrence !== "one_time" && e.status !== "cancelled"
  );

  // Normalized Monthly Software/Tool Burn Rate
  const monthlyRecurringBurn = recurringExpenses.reduce((sum, e) => {
    const amt = Number(e.amount) || 0;
    if (e.recurrence === "monthly") return sum + amt;
    if (e.recurrence === "annual") return sum + Math.round(amt / 12);
    if (e.recurrence === "quarterly") return sum + Math.round(amt / 3);
    return sum;
  }, 0);

  const filteredExpenses = state.expenses.filter((e) => {
    // Recurrence filter
    if (recurrenceFilter === "recurring" && (!e.recurrence || e.recurrence === "one_time")) return false;
    if (recurrenceFilter === "one_time" && e.recurrence && e.recurrence !== "one_time") return false;

    // Category filter
    if (categoryFilter !== "all" && e.category !== categoryFilter) return false;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchDesc = e.description?.toLowerCase().includes(term);
      const matchVendor = e.vendor?.toLowerCase().includes(term);
      const matchNotes = e.notes?.toLowerCase().includes(term);
      if (!matchDesc && !matchVendor && !matchNotes) return false;
    }
    return true;
  });

  const categories = [
    "all",
    "Software",
    "Infrastructure",
    "Domain & DNS",
    "Marketing & Ads",
    "Office & Operations",
    "Salaries & Contractors",
    "Professional Services",
    "Travel & Transport",
  ];

  const openClaudeCodePreset = () => {
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(nextMonth.getMonth() + 1);

    setActiveForm({
      kind: "expense",
      value: {
        id: "",
        date: today.toISOString().slice(0, 10),
        nextBillingDate: nextMonth.toISOString().slice(0, 10),
        category: "Software",
        vendor: "Anthropic / Claude",
        description: "Claude Code Subscription",
        amount: 1800,
        recurrence: "monthly",
        status: "active",
        paymentMethod: "Credit Card",
        notes: "AI Pair Programming & Code Generation for Engineering Team",
      },
    });
  };

  return (
    <>
      {/* Top Recurring SaaS & Tool Burn Overview Banner */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: "14px",
          marginBottom: "18px",
        }}
      >
        <div
          style={{
            background: "linear-gradient(135deg, #0d263e, #1a4267)",
            color: "#ffffff",
            padding: "16px 20px",
            borderRadius: "var(--radius-lg)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div>
            <span style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em", opacity: 0.8, fontWeight: 700 }}>
              Monthly Repeating Tool Burn (MRR)
            </span>
            <div style={{ fontSize: "24px", fontWeight: 800, marginTop: "4px", color: "var(--brand-gold)" }}>
              {money(monthlyRecurringBurn)} <small style={{ fontSize: "13px", fontWeight: 500, color: "#cbd5e1" }}>/month</small>
            </div>
            <div style={{ fontSize: "11.5px", opacity: 0.85, marginTop: "2px" }}>
              {recurringExpenses.length} active repeating subscriptions
            </div>
          </div>
          <div
            style={{
              width: "44px",
              height: "44px",
              borderRadius: "50%",
              background: "rgba(255, 255, 255, 0.12)",
              display: "grid",
              placeItems: "center",
              fontSize: "18px",
              color: "var(--brand-gold)",
            }}
          >
            <i className="fa-solid fa-arrows-rotate" />
          </div>
        </div>

        <div
          style={{
            background: "#ffffff",
            border: "1px solid var(--border-subtle)",
            padding: "16px 20px",
            borderRadius: "var(--radius-lg)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          <div>
            <span style={{ fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-muted)", fontWeight: 700 }}>
              Total Historical Outflow
            </span>
            <div style={{ fontSize: "24px", fontWeight: 800, marginTop: "4px", color: "#b91c1c" }}>
              {money(totalExpense)}
            </div>
            <div style={{ fontSize: "11.5px", color: "var(--text-muted)", marginTop: "2px" }}>
              Across {state.expenses.length} recorded items
            </div>
          </div>
          <button
            onClick={openClaudeCodePreset}
            style={{
              padding: "7px 12px",
              fontSize: "12px",
              background: "#f1f5f9",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-md)",
              fontWeight: 700,
              color: "var(--brand-navy)",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
            title="Quick add Claude Code subscription"
          >
            <i className="fa-solid fa-robot" style={{ color: "var(--brand-gold)" }} /> + Claude Code (₹1,800)
          </button>
        </div>
      </div>

      <section className="panel">
        <div
          className="panel-title"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div>
            <h3>Operating Expenses & Subscriptions</h3>
            <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "13px" }}>
              Track one-time costs, repeating tool subscriptions (Claude Code, SaaS), and vendor payouts
            </p>
          </div>

          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <button
              className="gold"
              onClick={() => setIsVoiceModalOpen(true)}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                fontWeight: 600,
              }}
              title="Add expense or subscription by voice"
            >
              <i className="fa-solid fa-microphone-lines" /> Voice Add Expense
            </button>

            <button
              onClick={() => {
                const today = new Date();
                const nextMonth = new Date(today);
                nextMonth.setMonth(nextMonth.getMonth() + 1);
                setActiveForm({
                  kind: "expense",
                  value: {
                    id: "",
                    date: today.toISOString().slice(0, 10),
                    nextBillingDate: nextMonth.toISOString().slice(0, 10),
                    category: "Software",
                    vendor: "",
                    description: "",
                    amount: 0,
                    recurrence: "monthly",
                    paymentMethod: "Credit Card",
                    notes: "",
                    status: "active",
                  },
                });
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                background: "#ffffff",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-md)",
                padding: "8px 14px",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                color: "var(--brand-navy)",
              }}
            >
              <i className="fa-solid fa-repeat" /> + Repeating Cost
            </button>

            <button
              className="primary"
              onClick={() =>
                setActiveForm({
                  kind: "expense",
                  value: {
                    id: "",
                    date: new Date().toISOString().slice(0, 10),
                    category: "Software",
                    vendor: "",
                    amount: 0,
                    recurrence: "one_time",
                    paymentMethod: "Bank Transfer",
                    notes: "",
                  },
                })
              }
            >
              <i className="fa-solid fa-plus" /> + Record Expense
            </button>
          </div>
        </div>

        {/* Voice Banner Callout */}
        <div
          style={{
            background: "linear-gradient(135deg, rgba(217, 119, 6, 0.08), rgba(245, 158, 11, 0.04))",
            border: "1px solid rgba(217, 119, 6, 0.2)",
            borderRadius: "var(--radius-md)",
            padding: "12px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "10px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "50%",
                background: "var(--brand-gold)",
                display: "grid",
                placeItems: "center",
                color: "#0d263e",
                fontSize: "14px",
              }}
            >
              <i className="fa-solid fa-microphone" />
            </span>
            <div>
              <strong style={{ fontSize: "13px", color: "var(--brand-navy)" }}>
                Fast Voice-to-Ledger Dictation
              </strong>
              <div style={{ fontSize: "12px", color: "var(--text-muted)" }}>
                Say &ldquo;Added Claude Code subscription 1800 rupees monthly via Credit Card&rdquo; to auto-record!
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsVoiceModalOpen(true)}
            style={{
              padding: "5px 12px",
              fontSize: "12px",
              background: "#ffffff",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-sm)",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Try Audio Entry →
          </button>
        </div>

        {/* Recurrence Type Filters (All vs Subscriptions vs One-Time) */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "14px", flexWrap: "wrap" }}>
          {[
            { id: "all", label: `All Outflows (${state.expenses.length})` },
            { id: "recurring", label: `🔄 Repeating Subscriptions (${recurringExpenses.length})` },
            { id: "one_time", label: "One-Time Expenses" },
          ].map((r) => (
            <button
              key={r.id}
              onClick={() => setRecurrenceFilter(r.id as any)}
              style={{
                padding: "6px 14px",
                fontSize: "12.5px",
                borderRadius: "var(--radius-full)",
                border: "1px solid",
                borderColor: recurrenceFilter === r.id ? "var(--brand-navy)" : "var(--border-subtle)",
                background: recurrenceFilter === r.id ? "var(--brand-navy)" : "#f8fafc",
                color: recurrenceFilter === r.id ? "#ffffff" : "var(--text-main)",
                fontWeight: recurrenceFilter === r.id ? 700 : 500,
                cursor: "pointer",
              }}
            >
              {r.label}
            </button>
          ))}
        </div>

        {/* Search & Category Filter */}
        <div
          className="toolbar"
          style={{
            margin: "0 0 16px 0",
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <div style={{ position: "relative", flex: 1, minWidth: "220px" }}>
            <i
              className="fa-solid fa-magnifying-glass"
              style={{
                position: "absolute",
                left: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "var(--text-faint)",
                fontSize: "13px",
              }}
            />
            <input
              type="text"
              placeholder="Search description, vendor, notes, Claude..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: "38px" }}
            />
          </div>

          <div
            className="filter-pills-scroll"
            style={{
              display: "flex",
              gap: "6px",
              overflowX: "auto",
              maxWidth: "100%",
              paddingBottom: "4px",
            }}
          >
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                style={{
                  padding: "5px 12px",
                  fontSize: "11.5px",
                  borderRadius: "16px",
                  border: "1px solid",
                  borderColor: categoryFilter === cat ? "var(--brand-navy)" : "var(--border-subtle)",
                  background: categoryFilter === cat ? "var(--brand-navy)" : "#ffffff",
                  color: categoryFilter === cat ? "#ffffff" : "#475569",
                  fontWeight: categoryFilter === cat ? 600 : 500,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                {cat === "all" ? "All Categories" : cat}
              </button>
            ))}
          </div>
        </div>

        {/* 1. Desktop Table View */}
        <div className="desktop-table-view table-wrap">
          <table>
            <thead>
              <tr>
                <th>Date / Renewal</th>
                <th>Description</th>
                <th>Vendor / Payee</th>
                <th>Category</th>
                <th>Cost Type</th>
                <th>Payment Mode</th>
                <th style={{ textAlign: "right" }}>Amount</th>
                <th style={{ textAlign: "right" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.length > 0 ? (
                filteredExpenses.map((e) => {
                  const isRecurring = e.recurrence && e.recurrence !== "one_time";

                  return (
                    <tr key={e.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{formatDate(e.date)}</div>
                        {isRecurring && e.nextBillingDate && (
                          <div style={{ fontSize: "11px", color: "var(--brand-gold)", fontWeight: 700, marginTop: "2px" }}>
                            Renews: {formatDate(e.nextBillingDate)}
                          </div>
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                          <b>{e.description || "—"}</b>
                          {e.voiceNote && (
                            <span
                              title={`Recorded via voice: "${e.voiceNote}"`}
                              style={{
                                fontSize: "10px",
                                color: "var(--brand-gold)",
                                background: "rgba(217, 119, 6, 0.1)",
                                padding: "1px 6px",
                                borderRadius: "10px",
                                fontWeight: 700,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "3px",
                              }}
                            >
                              <i className="fa-solid fa-microphone" /> Audio
                            </span>
                          )}
                        </div>
                        {e.notes && <div style={{ fontSize: "11px", color: "#64748b" }}>{e.notes}</div>}
                      </td>
                      <td>{e.vendor}</td>
                      <td>
                        <span className="badge">{e.category}</span>
                      </td>
                      <td>
                        {isRecurring ? (
                          <span
                            style={{
                              fontSize: "11px",
                              fontWeight: 700,
                              background: "#e0f2fe",
                              color: "#0369a1",
                              padding: "3px 8px",
                              borderRadius: "10px",
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            <i className="fa-solid fa-repeat" />{" "}
                            {e.recurrence === "monthly" ? "Monthly" : e.recurrence === "annual" ? "Annual" : "Quarterly"}
                          </span>
                        ) : (
                          <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>One-Time</span>
                        )}
                      </td>
                      <td>{e.paymentMethod || "Bank Transfer"}</td>
                      <td style={{ textAlign: "right", fontWeight: 700, color: "#0f172a" }}>
                        {money(e.amount)}
                        {isRecurring && (
                          <div style={{ fontSize: "10px", color: "var(--text-muted)", fontWeight: 500 }}>
                            /{e.recurrence === "annual" ? "yr" : "mo"}
                          </div>
                        )}
                      </td>
                      <td className="row-actions">
                        <button onClick={() => setActiveForm({ kind: "expense", value: e })}>
                          Edit
                        </button>
                        <button
                          className="danger"
                          onClick={() => {
                            if (confirm(`Delete expense record for ${e.description}?`)) {
                              deleteExpense(e.id);
                            }
                          }}
                        >
                          <i className="fa-solid fa-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={8} className="empty">
                    No expense records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* 2. Mobile Responsive Card List View */}
        <div className="mobile-cards-view">
          {filteredExpenses.length > 0 ? (
            filteredExpenses.map((e) => {
              const isRecurring = e.recurrence && e.recurrence !== "one_time";

              return (
                <div key={e.id} className="mobile-doc-card">
                  <div className="mobile-doc-top">
                    <span className="mobile-doc-num">{e.category}</span>
                    {isRecurring ? (
                      <span
                        style={{
                          fontSize: "11px",
                          fontWeight: 700,
                          background: "#e0f2fe",
                          color: "#0369a1",
                          padding: "2px 8px",
                          borderRadius: "10px",
                        }}
                      >
                        🔄 {e.recurrence === "monthly" ? "Monthly" : "Annual"}
                      </span>
                    ) : (
                      <span style={{ fontSize: "11px", color: "var(--text-muted)" }}>One-Time</span>
                    )}
                  </div>

                  <div className="mobile-doc-company">{e.description || e.vendor}</div>
                  <div className="mobile-doc-email">
                    Vendor: {e.vendor} • {e.paymentMethod}
                  </div>

                  {isRecurring && e.nextBillingDate && (
                    <div style={{ fontSize: "11.5px", color: "var(--brand-gold)", fontWeight: 700, margin: "4px 0" }}>
                      Renews: {formatDate(e.nextBillingDate)}
                    </div>
                  )}

                  {e.voiceNote && (
                    <div
                      style={{
                        margin: "6px 0",
                        fontSize: "11px",
                        background: "rgba(217,119,6,0.08)",
                        border: "1px solid rgba(217,119,6,0.2)",
                        borderRadius: "var(--radius-sm)",
                        padding: "4px 8px",
                        color: "#b45309",
                      }}
                    >
                      <i className="fa-solid fa-microphone" /> {e.voiceNote}
                    </div>
                  )}

                  <div className="mobile-doc-amount">
                    <small>{isRecurring ? "Repeating Cost" : "Recorded Outflow"}</small>
                    <strong style={{ color: "#b91c1c" }}>
                      {money(e.amount)}
                      {isRecurring && <small style={{ fontSize: "11px", color: "#64748b" }}> /{e.recurrence === "annual" ? "yr" : "mo"}</small>}
                    </strong>
                  </div>

                  <div className="mobile-doc-actions">
                    <button onClick={() => setActiveForm({ kind: "expense", value: e })}>
                      <i className="fa-solid fa-pen-to-square" /> Edit
                    </button>
                    <button
                      className="danger"
                      onClick={() => {
                        if (confirm(`Delete ${e.description}?`)) deleteExpense(e.id);
                      }}
                    >
                      <i className="fa-solid fa-trash" />
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="empty" style={{ padding: "30px 0" }}>
              No expenses recorded yet.
            </div>
          )}
        </div>
      </section>

      {/* Voice Expense Audio Modal */}
      <AudioExpenseModal isOpen={isVoiceModalOpen} onClose={() => setIsVoiceModalOpen(false)} />
    </>
  );
};
